import React from 'react';

const chapters = [
  { id: 'qa-context', label: 'Product context' },
  { id: 'qa-agents-map', label: 'Agents & people' },
  { id: 'qa-release-path', label: 'Eight-stage process' },
  { id: 'qa-controls', label: 'Quality controls' },
  { id: 'qa-pilot', label: 'Pilot & measures' },
];

function goToChapter(id) {
  const heading = document.getElementById(id);
  if (!heading) return;
  heading.scrollIntoView({ block: 'start' });
  heading.focus({ preventScroll: true });
}

function ChapterHeading({ id, number, title, children }) {
  return (
    <div className="section-heading qa-chapter-heading">
      <div><span className="eyebrow">Operating blueprint / {number}</span><h2 id={id} tabIndex={-1}>{title}</h2></div>
      <p>{children}</p>
    </div>
  );
}

export default function QaProposalDetails({ project }) {
  const proposal = project.proposal;
  return (
    <div className="qa-proposal">
      <div className="qa-proposal-note">
        <span className="eyebrow">Proposal / <time dateTime={proposal.dateTime}>{proposal.date}</time></span>
        <p>From manual execution to human review.</p>
        <span>Architecture and process prepared for approval. The one-team pilot is proposed; impact will be measured against the current manual flow.</span>
      </div>
      <nav className="qa-chapter-nav" aria-label="QA study chapters">
        {chapters.map((chapter, index) => <button key={chapter.id} type="button" onClick={() => goToChapter(chapter.id)}><span aria-hidden="true">0{index + 1}</span>{chapter.label}</button>)}
      </nav>

      <section className="qa-chapter" aria-labelledby="qa-context">
        <ChapterHeading id="qa-context" number="01" title="A requirement behind every run.">The Jira issue key connects code, product intent and test evidence. Versioned requirements keep the test history explainable.</ChapterHeading>
        <ol className="qa-context-chain">
          {proposal.traceability.map((item, index) => <li key={item.title}><span className="eyebrow">0{index + 1}</span><h3>{item.title}</h3><p>{item.detail}</p></li>)}
        </ol>
        <div className="qa-rule"><span className="eyebrow">Before the first test</span><p>A missing Jira key or PRD link fails the PR build with a reason and blocks merging. Every generated or rewritten test set needs human approval, including after a PRD change.</p></div>
      </section>

      <section className="qa-chapter" aria-labelledby="qa-agents-map">
        <ChapterHeading id="qa-agents-map" number="02" title="Clear jobs. Clear accountability.">One orchestrator and six specialists handle repeatable work. No agent approves its own output.</ChapterHeading>
        <div className="qa-orchestrator"><span className="eyebrow">Coordination layer</span><h3>Orchestrator</h3><p>{proposal.orchestrator}</p></div>
        <ol className="qa-agent-grid" aria-label="Six specialist agents">
          {proposal.agents.map((agent, index) => <li key={agent.name}><span className="eyebrow">Agent / 0{index + 1}</span><h3>{agent.name}</h3><p>{agent.task}</p></li>)}
        </ol>
        <div className="qa-human-decisions"><div><span className="eyebrow">Four principal human decisions</span><h3>People hold the gates.</h3></div><dl>{proposal.humanDecisions.map(item => <div key={item.role}><dt>{item.role}</dt><dd>{item.decision}</dd></div>)}</dl></div>
      </section>

      <section className="qa-chapter" aria-labelledby="qa-release-path">
        <ChapterHeading id="qa-release-path" number="03" title="From PR to production evidence.">Each stage has an owner and a condition for moving forward. Failed checks return to a defined response before the change continues.</ChapterHeading>
        <ol className="qa-workflow" aria-label="Eight-stage QA process">
          {proposal.workflow.map((stage, index) => <li key={stage.name}>
            <span className="qa-step-number" aria-hidden="true">0{index + 1}</span>
            <div className="qa-step-content"><h3>{stage.name}</h3><p>{stage.action}</p><dl><div><dt>Owner</dt><dd>{stage.owner}</dd></div><div><dt>Pass condition</dt><dd>{stage.gate}</dd></div></dl><div className="qa-step-response"><span className="eyebrow">Response & continuity</span><p>{stage.fallback}</p></div></div>
          </li>)}
        </ol>
      </section>

      <section className="qa-chapter qa-controls-chapter" aria-labelledby="qa-controls">
        <ChapterHeading id="qa-controls" number="04" title="The failure paths are part of the design.">Reliability comes from what happens when a test is flaky, a bug is disputed or a release needs an urgent fix. Open a field note for its policy.</ChapterHeading>
        <div className="qa-control-notes">
          {project.sections.map((section, index) => <details key={section.title} className="qa-control-note" open={index === 0}>
            <summary><span className="eyebrow" aria-hidden="true">0{index + 1}</span><h3>{section.title}</h3><span className="qa-note-toggle" aria-hidden="true" /></summary>
            <div className="qa-control-body">{section.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}{section.bullets.length > 0 && <ul>{section.bullets.map(bullet => <li key={bullet}>{bullet}</li>)}</ul>}</div>
          </details>)}
        </div>
      </section>

      <section className="qa-chapter" aria-labelledby="qa-pilot">
        <ChapterHeading id="qa-pilot" number="05" title="Prove it with one team first.">The proposal defines four rollout phases and three gates. It requests the first three phases with one team, then a pilot review before wider rollout.</ChapterHeading>
        <div className="qa-pilot-boundary"><span className="eyebrow">Pilot boundary</span><p>Keep the current manual flow available during phases 2 and 3. Compare pilot measures with the manual baseline at review, and seek approval before rolling out to all teams.</p></div>
        <div className="qa-measures-heading"><h3>Baseline first. Measure monthly.</h3><p>These are evaluation criteria. The proposal reports no baseline values, numerical targets or pilot results.</p></div>
        <dl className="qa-measure-grid">{proposal.pilotMeasures.map(measure => <div key={measure.name}><dt>{measure.name}</dt><dd>{measure.purpose}</dd></div>)}</dl>
        <div className="qa-approval-scope"><div><span className="eyebrow">Requested approval</span><h3>What the pilot needs.</h3></div><ol>{proposal.approvalScope.map(item => <li key={item}>{item}</li>)}</ol></div>
        <div className="qa-rule"><span className="eyebrow">Monthly quality review</span><p>Review production bugs, overruled agent bugs, flaky tests and every hotfix. Turn each finding into a new test case or rule, then use the next review to assess the process again.</p></div>
      </section>
    </div>
  );
}
